import { useExcelExport } from "@/hooks/useExcelExport";
import { AuditLogEntry } from "@/services/auditLogApi";
import { ACTION_LABELS } from "@/constants/audit";
import { summarizeChange } from "@/lib/auditDiff";

const COLUMNS = ["Timestamp", "User", "Action", "Stage", "Item", "Change", "Comment"];

function toRow(entry: AuditLogEntry): string[] {
  return [
    new Date(entry.created_at).toLocaleString(),
    entry.user_name ?? "",
    ACTION_LABELS[entry.action] ?? entry.action,
    entry.stage ?? "",
    entry.entity_name ?? entry.entity_id,
    summarizeChange(entry.before, entry.after),
    entry.comment ?? "",
  ];
}

function toRecord(entry: AuditLogEntry): Record<string, string> {
  return Object.fromEntries(COLUMNS.map((col, i) => [col, toRow(entry)[i]]));
}

function toCsvValue(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

export function useAuditLogExport() {
  const { exportToExcel } = useExcelExport();

  const exportToCsv = (entries: AuditLogEntry[], fileName = "audit-log.csv") => {
    const lines = [
      COLUMNS.map(toCsvValue).join(","),
      ...entries.map((entry) => toRow(entry).map(toCsvValue).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToXlsx = (entries: AuditLogEntry[], fileName = "audit-log.xlsx") => {
    exportToExcel(entries.map(toRecord), "Audit Log", fileName);
  };

  return { exportToCsv, exportToXlsx };
}
